import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { PopoverModule, Popover } from 'primeng/popover';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { AuthService } from '@auth/services/auth.service';
import { ThemeService } from '@shared/services/theme.service';
import { VersionService } from '@shared/services/version.service';
import { AlertasService } from '@shared/services/alertas.service';
import { AppVersion } from '@shared/models/version.t';
import { Alerta, AlertaCategoria } from '@shared/models/alerta.t';

/**
 * Formatea una fecha ISO como tiempo relativo en español coloquial
 * («hace 5 min», «hace 2 h», «hace 3 días»). Redondea hacia abajo; una fecha
 * inválida o futura cae en la representación vacía / «hace instantes».
 */
export function tiempoRelativo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const minutes = Math.floor((Date.now() - then) / 60_000);
  if (minutes < 1) return 'hace instantes';
  if (minutes < 60) return `hace ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} día${days === 1 ? '' : 's'}`;

  const months = Math.floor(days / 30);
  if (months < 12) return `hace ${months} mes${months === 1 ? '' : 'es'}`;

  const years = Math.floor(months / 12);
  return `hace ${years} año${years === 1 ? '' : 's'}`;
}

const CATEGORIA_LABELS: Record<AlertaCategoria, string> = {
  PUBLICAR_FORMULARIO: 'Publicar formulario',
  SUGERENCIA_CAMBIO: 'Sugerencia de cambio',
  // P04 (D11/T8): fallback genérico -- la fila de VALIDACION casi siempre
  // muestra `alerta.descripcion` directo (más específico, ver la plantilla),
  // así que esta entrada solo mantiene el `Record<AlertaCategoria, ...>`
  // exhaustivo para una categoría sin `descripcion` (caso límite).
  VALIDACION: 'Validación pendiente',
};

const CATEGORIA_ICONOS: Record<AlertaCategoria, string> = {
  PUBLICAR_FORMULARIO: 'pi pi-cloud-upload',
  SUGERENCIA_CAMBIO: 'pi pi-comment',
  VALIDACION: 'pi pi-flag',
};

@Component({
  selector: 'app-status-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DatePipe, AvatarModule, TooltipModule, PopoverModule, OverlayBadgeModule],
  templateUrl: './status-bar.html',
  styleUrl: './status-bar.css',
})
export class StatusBar {
  protected authService = inject(AuthService);
  protected themeService = inject(ThemeService);
  private versionService = inject(VersionService);
  private alertasService = inject(AlertasService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  /** F8: the popover's trigger -- focus is restored here `onPanelHide` (Escape,
   * outside click, or the panel's own item click all close it without PrimeNG moving
   * focus back on their own, which would otherwise strand it on `<body>`). */
  private avatarButtonRef = viewChild<ElementRef<HTMLButtonElement>>('avatarButton');

  protected isDarkMode = this.themeService.getDarkMode();
  protected claims = computed(() => this.authService.claims());

  private appVersion = signal<AppVersion | null>(null);
  /** Deployed app version string (build-baked), or null when unavailable. */
  protected appVersionLabel = computed(() => this.appVersion()?.version ?? null);

  /** Ids de alertas marcadas como leídas de forma optimista (antes de que el servidor confirme). */
  private localLeidaIds = signal<ReadonlySet<number>>(new Set());
  /** Ids de alertas VALIDACION marcadas como resueltas de forma optimista desde el botón
   * solo-ícono del popover (P04/T8) -- se quitan de la vista de inmediato; se revierte en
   * silencio si el POST falla (mismo criterio que `localLeidaIds`). */
  private localResueltaIds = signal<ReadonlySet<number>>(new Set());
  /** Panel de notificaciones abierto/cerrado — refleja los eventos onShow/onHide del popover. */
  protected panelOpen = signal(false);
  /** Refresco de claims en curso (disparado al abrir el panel). */
  protected panelLoading = signal(false);

  /** `claims().alertas` con las marcas de lectura/resolución optimistas aplicadas. */
  protected alertasView = computed<Alerta[]>(() => {
    const alertas = this.claims()?.alertas ?? [];
    const leidas = this.localLeidaIds();
    const resueltas = this.localResueltaIds();
    return alertas
      .filter(a => !resueltas.has(a.id))
      .map(a => (leidas.has(a.id) && a.estado === 'PENDIENTE' ? { ...a, estado: 'LEIDA' } : a));
  });

  protected alertasPendientes = computed(
    () => this.alertasView().filter(a => a.estado === 'PENDIENTE').length
  );

  /** F14 (MENOR-14): the badge/counter's DISPLAY text -- caps at "99+" so a 3+ digit
   * count doesn't blow out the small badge/pill. `avatarAriaLabel` below keeps the
   * exact count (more informative for screen-reader users, and there's no rendering
   * constraint to cap there). */
  protected alertasPendientesLabel = computed(() => {
    const n = this.alertasPendientes();
    return n > 99 ? '99+' : String(n);
  });

  protected avatarAriaLabel = computed(() => {
    const n = this.alertasPendientes();
    return n > 0 ? `Notificaciones, ${n} sin leer` : 'Notificaciones';
  });

  constructor() {
    this.versionService
      .getAppVersion()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: version => this.appVersion.set(version),
        error: () => this.appVersion.set(null),
      });
  }

  protected avatarLabel = computed(() => {
    const name = this.claims()?.nombre_completo;
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  });

  protected sessionExpiry = computed(() => this.authService.getTokenExpirationDate());

  protected locationLine = computed(() => {
    const entidad = this.claims()?.entidad;
    if (!entidad) return null;
    const parts = [entidad.departamento?.nombre];
    if (entidad.municipio?.nombre) parts.push(entidad.municipio.nombre);
    return parts.filter(Boolean).join(' · ');
  });

  protected avatarTooltip = computed(() => {
    const c = this.claims();
    if (!c) return '';
    const lines: string[] = [
      c.nombre_completo,
      c.correo_electronico,
      `Doc: ${c.numero_documento}`,
    ];
    if (c.entidad) {
      lines.push(c.entidad.nombre);
      lines.push(`${c.entidad.tipo} · ${c.entidad.codigo}`);
      const loc = [c.entidad.departamento?.nombre, c.entidad.municipio?.nombre]
        .filter(Boolean)
        .join(' · ');
      if (loc) lines.push(loc);
    }
    return lines.join('\n');
  });

  // F11 (MENOR-7): a category value the client doesn't recognize yet (a future
  // AlertaCategoria the backend started sending before this map was updated) must
  // never render `undefined` in the panel -- fall back to a generic label/icon.
  protected categoriaLabel(categoria: AlertaCategoria): string {
    return CATEGORIA_LABELS[categoria] ?? 'Notificación';
  }

  protected categoriaIcono(categoria: AlertaCategoria): string {
    return CATEGORIA_ICONOS[categoria] ?? 'pi pi-bell';
  }

  /**
   * «De {origen}» del ítem del panel. El fallback cuando no hay origen
   * difiere por categoría (brief, alcance de P04/T8): «Sistema» es correcto
   * para VALIDACION (las genera el motor, no una persona), pero
   * PUBLICAR_FORMULARIO/SUGERENCIA_CAMBIO siempre tienen un origen humano
   * hoy -- su fallback histórico «un usuario» no debía cambiar solo porque
   * VALIDACION ganó uno propio.
   */
  protected origenLabel(alerta: Alerta): string {
    if (alerta.usuario_origen_nombre) return alerta.usuario_origen_nombre;
    return alerta.categoria === 'VALIDACION' ? 'Sistema' : 'un usuario';
  }

  protected tiempoRelativo(iso: string): string {
    return tiempoRelativo(iso);
  }

  /**
   * CP3 review (menor): la línea `text-xs` con la validación que pide el
   * brief -- distinta de la de GESTOR/ASIGNADO removida en F4 (esa repetía
   * `alerta_nombre`, que YA está en `descripcion`; esto es información
   * NUEVA, la validación puntual que disparó la violación asignada).
   * `datos.validacion_nombre` solo lo manda `notificar_asignado` (rol
   * ASIGNADO) -- una notificación GESTOR o de otra categoría no lo trae
   * nunca, así que basta con comprobar el tipo acá sin repetir el chequeo de
   * `rol`/`categoria` del llamador.
   */
  protected validacionNombreDe(alerta: Alerta): string | null {
    const nombre = alerta.datos?.['validacion_nombre'];
    return typeof nombre === 'string' && nombre.length > 0 ? nombre : null;
  }

  /**
   * Abre/cierra el panel de notificaciones y, al abrir, refresca claims (única
   * fuente de `alertas`; sin polling en ningún otro lugar). El popover se abre
   * de inmediato — el spinner del header cubre la ventana del refresco.
   */
  protected onAvatarClick(event: Event, panel: Popover): void {
    panel.toggle(event);
    this.panelOpen.set(panel.overlayVisible);
    if (panel.overlayVisible) {
      this.panelLoading.set(true);
      this.authService
        .loadClaims()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.panelLoading.set(false);
          // F5 (MAYOR-6): server truth wins over the optimistic overlay -- once fresh
          // claims land, drop every locally-flipped id. Without this, an alerta the
          // author RESENT after the user had already read the old one (same id, back
          // to PENDIENTE server-side) would stay stuck showing LEIDA forever, since
          // `alertasView()` would keep masking the fresh PENDIENTE with the stale
          // optimistic flip. The optimistic flip + silent revert for the still-in-
          // flight `marcarLeida()` window (see `onAlertaClick`) is untouched.
          this.localLeidaIds.set(new Set());
          this.localResueltaIds.set(new Set());
        });
    }
  }

  /** Mantiene `panelOpen` sincronizado cuando el popover se cierra por otra vía (Escape, click afuera).
   * F8: también devuelve el foco al botón que lo abrió -- PrimeNG no lo hace por su
   * cuenta, y sin esto el foco queda perdido en `<body>` (falla de gestión de foco,
   * WCAG AA). `?.` cubre el caso en que el propio status-bar ya se desmontó. */
  protected onPanelHide(): void {
    this.panelOpen.set(false);
    this.avatarButtonRef()?.nativeElement.focus();
  }

  /**
   * Marca la alerta como leída (optimista; revierte en silencio si falla) y
   * navega al destino que corresponda. La navegación procede siempre, incluso
   * si la marca de lectura falla en el servidor.
   *
   * P04 (D11, T8): para `categoria === 'VALIDACION'` el destino depende de
   * `datos.rol` -- GESTOR abre «Generadas» prefiltrada por su alerta
   * (`datos.alerta_id`), ASIGNADO abre el detalle de SU violación puntual
   * (`datos.violacion_id`). Un `rol` desconocido (payload futuro) no navega
   * a ningún lado -- mejor quedarse quieto que mandar al usuario a una ruta
   * sin sentido.
   */
  protected onAlertaClick(alerta: Alerta, panel: Popover): void {
    if (alerta.estado === 'PENDIENTE') {
      this.localLeidaIds.update(ids => new Set(ids).add(alerta.id));
      this.alertasService
        .marcarLeida(alerta.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: () => {
            this.localLeidaIds.update(ids => {
              const next = new Set(ids);
              next.delete(alerta.id);
              return next;
            });
          },
        });
    }

    panel.hide();
    this.panelOpen.set(false);

    if (alerta.categoria === 'VALIDACION') {
      const rol = alerta.datos?.['rol'];
      if (rol === 'GESTOR') {
        this.router.navigate(['/configuracion/alertas/generadas'], {
          queryParams: { alerta_id: alerta.datos?.['alerta_id'] },
        });
      } else if (rol === 'ASIGNADO') {
        this.router.navigate(['/alertas/violaciones', alerta.datos?.['violacion_id']]);
      }
      return;
    }

    if (alerta.form_id != null) {
      this.router.navigate(['/composer/form', alerta.form_id]);
    }
  }

  /**
   * Botón solo-ícono «Marcar resuelta» del popover (P04, brief «Notificaciones»)
   * -- separado del click del ítem (no anidado dentro de su `<button>`: dos
   * controles interactivos anidados es un error de a11y/AXE). Optimista: la
   * fila desaparece de `alertasView()` de inmediato; revierte en silencio si
   * el POST falla, mismo criterio que `onAlertaClick`.
   */
  protected onResolverClick(alerta: Alerta, event: Event): void {
    event.stopPropagation();
    this.localResueltaIds.update(ids => new Set(ids).add(alerta.id));
    this.alertasService
      .marcarResuelta(alerta.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.localResueltaIds.update(ids => {
            const next = new Set(ids);
            next.delete(alerta.id);
            return next;
          });
        },
      });
  }

}
